/**
 * This function adapts a select component to handle an "All" selection state without actually triggering the selection of all input fields.
 * It's used when many options are available, and there is no need of registering all of them individually.
 * Useful when the api supports a way to indicate "all" items are selected without sending all ids like -1 or a specific flag.
 *
 * @param formValue
 * @param onFormChange
 * @param totalCount
 * @param enabled
 * @returns
 */

import { ALL_LIT, Primitive } from '@/legacy/utils/seleactAll';

type OptionLike = Primitive | { value: Primitive };
const toId = (x: OptionLike): Primitive =>
	typeof x === 'object' && x !== null ? (x as any).value : (x as Primitive);

export const adaptSelectProps = (
	formValue: Primitive[] | typeof ALL_LIT,
	onFormChange: (v: Primitive[] | typeof ALL_LIT) => void,
	totalCount: number,
	enabled: boolean,
) => {
	if (!enabled) {
		// plain mode
		const coreValue = Array.isArray(formValue) ? (formValue as Primitive[]) : [];
		const coreOnChange = (next: OptionLike[] | OptionLike) => {
			const arr = Array.isArray(next) ? next.map(toId) : [toId(next)];
			onFormChange(arr);
		};
		return {
			coreValue,
			coreOnChange,
			isAll: totalCount > 0 && coreValue.length >= totalCount,
			setAll: () => {},
			clearAll: () => onFormChange([]),
		};
	}

	// sentinel mode
	const isAll = formValue === ALL_LIT;
	const coreValue = isAll ? [] : (formValue as Primitive[]);

	const commit = (arr: Primitive[]) => {
		if (arr.length === 0) return onFormChange([]);
		if (totalCount && arr.length >= totalCount) return onFormChange(ALL_LIT);
		onFormChange(arr);
	};

	const coreOnChange = (next: OptionLike[] | OptionLike) => {
		const arr = Array.isArray(next) ? next.map(toId) : [toId(next)];
		commit(arr);
	};

	return {
		coreValue,
		coreOnChange,
		isAll,
		setAll: () => onFormChange(ALL_LIT),
		clearAll: () => onFormChange([]),
	};
};
